import React, { useEffect, useState } from 'react';
import { useAuth } from "react-oidc-context";
import { Loading } from '../components/ui';
const ProtectedRoute = ({ children }) => {
  const auth = useAuth();
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    // Check if token exists but is expired
    const checkAndRefreshToken = async () => {
      if (auth.isAuthenticated && auth.user) {
        try {
          // Check if token is about to expire (in the next 5 minutes)
          const expiresAt = auth.user.expires_at;
          const isExpiringSoon = expiresAt && (expiresAt - Date.now() / 1000 < 300);

          if (isExpiringSoon) {
            console.log("Token expiring soon, refreshing silently...");
            await auth.signinSilent();
          }
        } catch (e) {
          console.error("Token refresh error:", e);
        }
      }
    };

    checkAndRefreshToken();
  }, [auth]);

  // Handle auth errors - clear storage and redirect for state mismatch errors
  useEffect(() => {
    const error = authError || auth.error;
    if (error) {
      const errorMessage = error.message || '';
      // Check for state mismatch or storage-related errors
      if (errorMessage.includes('No matching state found') ||
          errorMessage.includes('state') ||
          errorMessage.includes('storage')) {
        console.log('Auth state error detected, clearing storage and redirecting to login...');

        // Clear OIDC-related storage
        const keysToRemove = [];
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i);
          if (key && (key.startsWith('oidc.') || key.includes('oidc'))) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => sessionStorage.removeItem(key));

        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && (key.startsWith('oidc.') || key.includes('oidc'))) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key));

        // Clear URL params and redirect
        window.history.replaceState({}, document.title, window.location.pathname);

        // Small delay to ensure storage is cleared, then redirect
        setTimeout(() => {
          window.location.href = window.location.pathname;
        }, 100);
      }
    }
  }, [authError, auth.error]);

  // Only try redirect if not checking session, not loading, and not authenticated
  // Skip redirect if we have a code param - OIDC callback is in progress
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const hasCodeParam = urlParams.has('code');

    // Wait for auth to finish loading before deciding to redirect
    if (!auth.isLoading && !auth.isAuthenticated && !hasCodeParam) {
      try {
        auth.signinRedirect();
      } catch (e) {
        console.error("Redirect error:", e);
        setAuthError(e);
      }
    }
    if (auth.isAuthenticated && hasCodeParam){
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [auth]);

  if (auth.isLoading) {
    return <Loading />;
  }

  if (authError || auth.error) {
    const error = authError || auth.error;
    const errorMessage = error.message || '';

    // Don't show error UI for state mismatch - we're auto-redirecting
    if (errorMessage.includes('No matching state found') ||
        errorMessage.includes('state') ||
        errorMessage.includes('storage')) {
      return <Loading />;
    }

    return (
      <div className="auth-error">
        <h3>Authentication Error</h3>
        <p>{errorMessage}</p>
        <button onClick={() => window.location.reload()}>
          Try Again
        </button>
      </div>
    );
  }

  if (!auth.isAuthenticated) {
    return <Loading />;
  }

  return children;
};

export default ProtectedRoute;
